import type { CSSProperties, ReactElement } from 'react'
import { Form, RadioButtonField } from '@epages/react-components'
import { useId, useMemo, useRef, useState } from 'react'
import Immutable from 'immutable'
import cc from 'classcat'
import loadable from '@loadable/component'

import type { BackgroundColorSettings, BackgroundWidthSettings } from './Block'
import { Banner } from '../../Banner/Banner'
import { EditorLink } from '../../EditorLink/EditorLink'
import { ExpandableRadioSection } from '../../formComponents/ExpandableRadioSection/ExpandableRadioSection'
import { defaultBackgroundColorSettings, getDefaultBackgroundWidthSettings } from './Block'
import { getRefColors, resolveRefColor } from '../../../utils/color-refs'
import { toPresetColors, useColorNameFormatters, useColorOptions } from '../../../utils/hooks/colorSettings'
import Button from '../../formComponents/Button'
import ButtonRow from '../../formComponents/ButtonRow'
import FormGroup from '../../formComponents/FormGroup'
import ImageField from './plugins/image/ImageField'
import useHelpCenterLink from '../../../utils/hooks/useHelpCenterLink'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ './SettingsLayer'))
const ColorInput = loadable(
  () => import(/* webpackChunkName: "editor" */ '../../formComponents/ColorInput/ColorInput'),
  {
    resolveComponent: (components) => components.ColorInput,
  },
)

type Props = {
  currentColorSettings: BackgroundColorSettings
  currentWidthSettings: BackgroundWidthSettings
  onColorSettingsChange: (settings: BackgroundColorSettings) => void
  onWidthSettingsChange: (settings: BackgroundWidthSettings) => void
  onCancel: () => void
  onSave: () => void
  config: {
    plugins?: Record<
      string,
      {
        config?: {
          storeFile?: (file: File) => Promise<any>
          imageUrl?: (url: string) => string
          previewUrl?: (url: string) => string
        }
      }
    >
  }
  themeName: string
} & TranslateProps

export default function BlockBackgroundSettings({
  t,
  currentColorSettings,
  currentWidthSettings,
  onColorSettingsChange,
  onWidthSettingsChange,
  onSave,
  onCancel,
  config,
  themeName,
}: Readonly<Props>): ReactElement {
  const [showBlockBackgroundSettings, setShowBlockBackgroundSettings] = useState(false)
  const [activeTab, setActiveTab] = useState<'backgroundStyle' | 'backgroundWidth'>('backgroundStyle')
  const [imageError, setImageError] = useState<(Error & { code: number }) | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
  const isUnsupportedMediaType = imageError?.code === 415
  const idPrefix = useId()
  const styleRadioName = useId()

  const {
    backgroundImage,
    backgroundImageAlignment,
    backgroundType,
    gradientEndColor,
    gradientEndColorRef,
    gradientOrientation,
    gradientStartColor,
    gradientStartColorRef,
    showGradientMidColor,
    backgroundOverlayOpacity,
  } = currentColorSettings
  const imageConfig = config?.plugins?.['epages.image']?.config
  const tBackground = (key: string) => t(`backgroundButton.settingsLayer.backgroundStyleRadioButtons.${key}`)

  const colorNameFormatters = useColorNameFormatters()
  const colorOptions = useColorOptions(colorNameFormatters)
  const presetColors = toPresetColors(colorOptions)
  const refColors = getRefColors(presetColors)

  // Resolution plus the paired writers for one color field: a custom color
  // takes precedence over the ref, each writer nulls the other field, and a
  // ref missing from the palette resolves to the field default with no
  // selection.
  const colorFieldProps = (
    field: 'backgroundColor' | 'gradientStartColor' | 'gradientMidColor' | 'gradientEndColor',
  ) => {
    const refField = `${field}Ref` as const
    return {
      ...resolveRefColor(currentColorSettings[field], currentColorSettings[refField], refColors, {
        fallback: defaultBackgroundColorSettings[field],
      }),
      presetColors,
      onChange: (color: string) => onColorSettingsChange({ ...currentColorSettings, [field]: color, [refField]: null }),
      onRefChange: (ref: string) => onColorSettingsChange({ ...currentColorSettings, [field]: null, [refField]: ref }),
    }
  }

  const backgroundColorField = colorFieldProps('backgroundColor')
  const gradientStartColorField = colorFieldProps('gradientStartColor')
  const gradientMidColorField = colorFieldProps('gradientMidColor')
  const gradientEndColorField = colorFieldProps('gradientEndColor')

  const saveColor = () => {
    setShowBlockBackgroundSettings(false)
    setActiveTab('backgroundStyle')
    onSave()
  }

  const cancelHandler = () => {
    onCancel()
    setShowBlockBackgroundSettings(false)
    setActiveTab('backgroundStyle')
  }

  const handleSwitch = (event) => {
    event.preventDefault()
    onColorSettingsChange({
      ...currentColorSettings,
      gradientStartColor: gradientEndColor,
      gradientStartColorRef: gradientEndColorRef,
      gradientEndColor: gradientStartColor,
      gradientEndColorRef: gradientStartColorRef,
    })
  }

  const toggleColorSettings = () => {
    if (showBlockBackgroundSettings) {
      cancelHandler()
    } else {
      setShowBlockBackgroundSettings(true)
    }
  }

  const handleReset = () => {
    onColorSettingsChange(defaultBackgroundColorSettings)
  }

  const handleSolidColorSelection = () => {
    const newColorSettings = { ...currentColorSettings, backgroundType: 'color' as const }
    onColorSettingsChange(newColorSettings)

    // If switching to solid color and it's transparent, reset width to theme default
    if (backgroundColorField.value === 'transparent') {
      const defaults = getDefaultBackgroundWidthSettings(themeName)
      onWidthSettingsChange({
        ...defaults,
        isUserModified: false,
      })
    }
  }

  const handleBackgroundImageChange = (data: ImmutableMap) => {
    const imageData = data.get('backgroundImage') || Immutable.Map()
    const nextBackgroundImage = {
      src: imageData.get('src', ''),
      width: imageData.get('width', null),
      height: imageData.get('height', null),
    }
    const nextBackgroundImageAlignment = data.get('backgroundImageAlignment', backgroundImageAlignment)
    onColorSettingsChange({
      ...currentColorSettings,
      backgroundImage: nextBackgroundImage,
      backgroundImageAlignment: nextBackgroundImageAlignment,
    })
  }

  const backgroundImageValue = useMemo(
    () =>
      Immutable.Map({
        backgroundImage: Immutable.Map({
          src: backgroundImage?.src ?? defaultBackgroundColorSettings.backgroundImage.src,
          width: backgroundImage?.width ?? defaultBackgroundColorSettings.backgroundImage.width,
          height: backgroundImage?.height ?? defaultBackgroundColorSettings.backgroundImage.height,
        }),
        backgroundImageAlignment: backgroundImageAlignment ?? defaultBackgroundColorSettings.backgroundImageAlignment,
      }),
    [backgroundImage?.src, backgroundImage?.width, backgroundImage?.height, backgroundImageAlignment],
  )

  const handleWidthChange = (width: 'content' | 'full') => {
    onWidthSettingsChange({
      ...currentWidthSettings,
      width,
      isUserModified: true,
    })
  }

  const imageBackgroundDisabled = !backgroundImage?.src
  const overlayOpacitySliderValue = backgroundOverlayOpacity ?? 0

  const imageAlignmentRadioButtons = [
    {
      label: tBackground('image.imageAlignment.options.top'),
      value: 'top',
    },
    {
      label: tBackground('image.imageAlignment.options.bottom'),
      value: 'bottom',
    },
    {
      label: tBackground('image.imageAlignment.options.center'),
      value: 'center',
    },
  ]

  const styleSectionProps = {
    radioName: styleRadioName,
    className: 'background-style-container',
    headerClassName: 'ep-form-row-label background-style-label',
    bodyClassName: 'background-style-expanded-container',
  }

  const styleTabBody = (
    <>
      <div className="background-style-wrapper">
        <ExpandableRadioSection
          label={tBackground('solid.label')}
          active={backgroundType === 'color'}
          onActivate={handleSolidColorSelection}
          {...styleSectionProps}
        >
          <div className="background-style-expanded-content ep-form-row-label">
            <label htmlFor={`${idPrefix}-backgroundColor`}>{tBackground('solid.colorPicker.label')}</label>
            <ColorInput
              {...backgroundColorField}
              label={tBackground('solid.colorPicker.label')}
              inputHtmlId={`${idPrefix}-backgroundColor`}
              side="left"
              offsetDistance={45}
            />
          </div>
        </ExpandableRadioSection>
        <ExpandableRadioSection
          label={tBackground('gradient.label')}
          active={backgroundType === 'gradient'}
          onActivate={() => onColorSettingsChange({ ...currentColorSettings, backgroundType: 'gradient' })}
          {...styleSectionProps}
        >
          <div className="background-style-expanded-content gradient-background">
            <fieldset>
              <legend className="sr-only">{tBackground('gradient.numberRadioButtons.accessibilityLabel')}</legend>
              <label htmlFor="twoColors">
                <input
                  type="radio"
                  id="twoColors"
                  checked={!showGradientMidColor}
                  onChange={() => onColorSettingsChange({ ...currentColorSettings, showGradientMidColor: false })}
                />
                {tBackground('gradient.numberRadioButtons.twoColors')}
              </label>
              <label htmlFor="threeColors">
                <input
                  type="radio"
                  id="threeColors"
                  checked={showGradientMidColor}
                  onChange={() => onColorSettingsChange({ ...currentColorSettings, showGradientMidColor: true })}
                />
                {tBackground('gradient.numberRadioButtons.threeColors')}
              </label>
            </fieldset>
            <button className="gradient-direction-switch" onClick={handleSwitch}>
              {tBackground('gradient.switchColorsButton.label')}
            </button>
            <div
              className="gradient-bar"
              style={{
                background: showGradientMidColor
                  ? `linear-gradient( 90deg, ${gradientStartColorField.value}, ${gradientMidColorField.value}, ${gradientEndColorField.value})`
                  : `linear-gradient( 90deg, ${gradientStartColorField.value}, ${gradientEndColorField.value})`,
              }}
            ></div>
            <div className="gradient-colors">
              <ColorInput
                {...gradientStartColorField}
                label={tBackground('gradient.startColor.accessibilityLabel')}
                offsetDistance={45}
                side="left"
                align="end"
              />
              {showGradientMidColor && (
                <ColorInput
                  {...gradientMidColorField}
                  label={tBackground('gradient.midColor.accessibilityLabel')}
                  offsetDistance={45}
                  side="left"
                  align="end"
                />
              )}
              <ColorInput
                {...gradientEndColorField}
                label={tBackground('gradient.endColor.accessibilityLabel')}
                offsetDistance={45}
                side="left"
                align="end"
              />
            </div>
            <fieldset className="gradient-orientation">
              <legend className="gradient-orientation-legend ep-form-row-label">
                {tBackground('gradient.orientationRadioButtons.label')}
              </legend>
              <div className="gradient-orientation-options">
                <span>
                  <input
                    type="radio"
                    id="horizontal"
                    checked={gradientOrientation === '90deg'}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '90deg' })}
                  />
                  <label htmlFor="horizontal">{tBackground('gradient.orientationRadioButtons.horizontal')}</label>
                </span>
                <span>
                  <input
                    type="radio"
                    id="vertical"
                    checked={gradientOrientation === '180deg'}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '180deg' })}
                  />
                  <label htmlFor="vertical">{tBackground('gradient.orientationRadioButtons.vertical')}</label>
                </span>
                <span>
                  <input
                    type="radio"
                    id="diagonal"
                    checked={gradientOrientation === '45deg'}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '45deg' })}
                  />
                  <label htmlFor="diagonal">{tBackground('gradient.orientationRadioButtons.diagonal')}</label>
                </span>
              </div>
            </fieldset>
          </div>
        </ExpandableRadioSection>
        <ExpandableRadioSection
          label={tBackground('image.label')}
          active={backgroundType === 'image'}
          onActivate={() => onColorSettingsChange({ ...currentColorSettings, backgroundType: 'image' })}
          {...styleSectionProps}
        >
          <div className="background-style-expanded-content">
            {imageError && (
              <div className="dali-settingslayer-element">
                <Banner type="danger" role="alert">
                  {imageError.message}{' '}
                  {isUnsupportedMediaType && (
                    <EditorLink href={helpCenterUrl} type="external">
                      {t(':components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
                    </EditorLink>
                  )}
                </Banner>
              </div>
            )}
            <Form name="blockBackgroundImage" value={backgroundImageValue} onChange={handleBackgroundImageChange}>
              <div className="ep-form-row">
                <ImageField
                  name="backgroundImage"
                  storeFile={imageConfig?.storeFile}
                  imageUrl={imageConfig?.imageUrl}
                  previewUrl={imageConfig?.previewUrl}
                  showDeleteButton
                  className="dali-settingslayer-element"
                  onError={(error) => setImageError(error)}
                />
              </div>
              <FormGroup
                name="backgroundImageAlignment"
                label={tBackground('image.imageAlignment.label')}
                className={cc([{ disabled: !backgroundImage?.src }])}
              >
                <RadioButtonField
                  className="image-background-alignment"
                  name="backgroundImageAlignment"
                  buttons={imageAlignmentRadioButtons}
                  disabled={!backgroundImage?.src}
                />
              </FormGroup>
              <div className={cc(['ep-form-row', { disabled: imageBackgroundDisabled }])}>
                <label htmlFor="background-overlay-opacity" className="dali-settingslayer-label">
                  {tBackground('image.overlayOpacitySlider.label')}
                </label>
                <div className="dali-range-input-with-numeric">
                  <input
                    id="background-overlay-opacity"
                    type="range"
                    className="dali-range-input"
                    min={0}
                    max={100}
                    step={1}
                    value={overlayOpacitySliderValue}
                    disabled={imageBackgroundDisabled}
                    style={
                      {
                        '--min': 0,
                        '--max': 100,
                        '--val': overlayOpacitySliderValue,
                      } as CSSProperties
                    }
                    onChange={(e) =>
                      onColorSettingsChange({
                        ...currentColorSettings,
                        backgroundOverlayOpacity: Number(e.target.value),
                      })
                    }
                  />
                  <input
                    type="number"
                    className="dali-range-output"
                    min={0}
                    max={100}
                    step={1}
                    value={overlayOpacitySliderValue}
                    disabled={imageBackgroundDisabled}
                    onChange={(e) => {
                      const val = Number(e.target.value)
                      if (val >= 0 && val <= 100) {
                        onColorSettingsChange({
                          ...currentColorSettings,
                          backgroundOverlayOpacity: val,
                        })
                      }
                    }}
                  />
                </div>
              </div>
            </Form>
          </div>
        </ExpandableRadioSection>
      </div>
      <button type="button" className="reset-button" onClick={handleReset}>
        {t('backgroundButton.settingsLayer.resetButton.label')}
      </button>
    </>
  )

  const widthTabBody = (
    <fieldset>
      <legend className="ep-form-row-small-margin ep-form-row-label">
        {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.label')}
      </legend>
      <div className="background-width-options">
        <label>
          <input
            type="radio"
            id="content-width"
            checked={currentWidthSettings.width === 'content'}
            onChange={() => handleWidthChange('content')}
          />
          {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.contentWidthRadioButton')}
        </label>
        <label>
          <input
            type="radio"
            id="full-width"
            checked={currentWidthSettings.width === 'full'}
            onChange={() => handleWidthChange('full')}
          />
          {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.fullWidthRadioButton')}
        </label>
      </div>
    </fieldset>
  )

  const tabs = [
    {
      name: 'backgroundStyle',
      title: t('backgroundButton.settingsLayer.backgroundStyle.title'),
      active: activeTab === 'backgroundStyle',
      onClick: (name: string) => setActiveTab(name as 'backgroundStyle' | 'backgroundWidth'),
    },
    {
      name: 'backgroundWidth',
      title: t('backgroundButton.settingsLayer.backgroundWidth.title'),
      active: activeTab === 'backgroundWidth',
      onClick: (name: string) => setActiveTab(name as 'backgroundStyle' | 'backgroundWidth'),
    },
  ]
  const footer = (
    <ButtonRow>
      <Button type="button" variant="outline" onClick={cancelHandler}>
        {t('backgroundButton.settingsLayer.cancelButton.label')}
      </Button>
      <Button type="button" onClick={saveColor}>
        {t('backgroundButton.settingsLayer.saveButton.label')}
      </Button>
    </ButtonRow>
  )

  return (
    <>
      <button
        ref={buttonRef}
        className="dali-block-actionbar-button dali-block-actionbar-button-background-color"
        title={t('backgroundButton.label')}
        onClick={() => toggleColorSettings()}
      />
      <SettingsLayer
        open={showBlockBackgroundSettings}
        className="dali-settingslayer-block-background"
        virtualRef={buttonRef}
        side="right"
        onEscapeKeyDown={cancelHandler}
      >
        {({ renderLayout }) =>
          renderLayout({
            title: t(`backgroundButton.settingsLayer.${activeTab}.title`),
            tabs,
            body: activeTab === 'backgroundStyle' ? styleTabBody : widthTabBody,
            footer,
          })
        }
      </SettingsLayer>
    </>
  )
}
